import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Key,
  Trash2,
  Copy,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Plus,
  Search,
  Activity
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { formatDistanceToNow } from 'date-fns'
import type { Restaurant } from '@/types'

interface ApiKey {
  _id: string
  keyPrefix: string
  name: string
  restaurant: Restaurant
  isActive: boolean
  lastUsedAt?: Date
  expiresAt?: Date
  permissions: string[]
  rateLimit: { requestsPerMinute: number; requestsPerDay: number }
  createdAt: Date
}

export default function ApiKeyManagementScreen() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Generate API Key Modal
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('')
  const [keyName, setKeyName] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  // API Key Display Modal (one-time display)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [isKeyDisplayOpen, setIsKeyDisplayOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [keysResponse, restaurantsResponse] = await Promise.all([
        ApiService.listApiKeys(),
        ApiService.getAllRestaurants()
      ])

      if (keysResponse.success) {
        setApiKeys(keysResponse.data)
      }

      if (restaurantsResponse.success) {
        setRestaurants(restaurantsResponse.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load API keys')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateKey = async () => {
    if (!selectedRestaurant || !keyName) {
      setError('Please select a restaurant and enter a key name')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const response = await ApiService.generateApiKey(selectedRestaurant, {
        name: keyName,
        permissions: ['validate_subscription'],
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerDay: 10000
        }
      })

      if (response.success) {
        setGeneratedKey(response.data.apiKey)
        setIsKeyDisplayOpen(true)
        setIsGenerateModalOpen(false)
        setSelectedRestaurant('')
        setKeyName('')
        loadData() // Refresh the list
      } else {
        setError(response.message || 'Failed to generate API key')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate API key')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleRevokeKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return
    }

    try {
      const response = await ApiService.revokeApiKey(keyId)
      if (response.success) {
        loadData() // Refresh the list
      } else {
        setError(response.message || 'Failed to revoke API key')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke API key')
    }
  }

  const filteredKeys = apiKeys.filter(key =>
    key.restaurant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    key.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    key.keyPrefix.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Key Management</h1>
          <p className="text-muted-foreground mt-2">
            Manage API keys for restaurant POS systems to validate subscriptions
          </p>
        </div>
        <Button onClick={() => setIsGenerateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Generate API Key
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total API Keys</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{apiKeys.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Keys</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {apiKeys.filter(k => k.isActive).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revoked Keys</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {apiKeys.filter(k => !k.isActive).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by restaurant, name, or key prefix..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            List of all API keys with their status and usage information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No API keys found matching your search' : 'No API keys generated yet'}
              </div>
            ) : (
              filteredKeys.map((key) => (
                <div
                  key={key._id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{key.name}</h3>
                      {key.isActive ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          Revoked
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Key className="h-3 w-3" />
                        {key.keyPrefix}...
                      </span>
                      <span>Restaurant: {key.restaurant?.name || 'Unknown'}</span>
                      {key.lastUsedAt && (
                        <span className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Last used {formatDistanceToNow(new Date(key.lastUsedAt), { addSuffix: true })}
                        </span>
                      )}
                      {!key.lastUsedAt && (
                        <span className="text-amber-600">Never used</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Rate limit: {key.rateLimit.requestsPerMinute} req/min, {key.rateLimit.requestsPerDay} req/day
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {key.isActive && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRevokeKey(key._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Generate API Key Modal */}
      <Dialog open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate New API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for a restaurant to use in their POS system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="restaurant">Restaurant</Label>
              <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants.map((restaurant) => (
                    <SelectItem key={restaurant._id} value={restaurant._id}>
                      {restaurant.name} ({restaurant.databaseName})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="keyName">Key Name</Label>
              <Input
                id="keyName"
                placeholder="e.g., Production Key, Test Key"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </div>
            <Alert>
              <AlertDescription>
                The API key will be shown only once after generation. Make sure to save it securely.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGenerateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateKey} disabled={isGenerating}>
              {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Display Modal (One-time) */}
      <Dialog open={isKeyDisplayOpen} onOpenChange={setIsKeyDisplayOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>API Key Generated</DialogTitle>
            <DialogDescription>
              Save this API key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This API key will only be shown once. Copy it now and store it securely.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={generatedKey || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyKey}
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsKeyDisplayOpen(false)
                setGeneratedKey(null)
                setCopied(false)
              }}
            >
              I've Saved the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
